/*
  Execution template, wave 3 (screen 10 ideas 3 and 8): each template task carries how long it takes (working
  days, used by Auto-plan) and which tasks must finish first (its dependencies — the Gantt draws them and finds
  the critical path). The backend keeps both (project_plan.normalize_template_plan) and cuts any cycle.
*/
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export function TemplateDurationInput({ item, onChange }) {
  return (
    <label className="flex items-center gap-1.5">
      <Input
        type="number"
        min={1}
        max={365}
        inputMode="numeric"
        value={item.duration_days ?? ''}
        onChange={(event) => onChange(event.target.value === '' ? null : Math.max(1, Math.min(365, Math.round(Number(event.target.value) || 1))))}
        aria-label={`Working days for ${item.name || 'this task'}`}
        className="h-9 w-[68px] rounded-sm border-zinc-700 bg-zinc-800/50 text-right text-zinc-100"
      />
      <span className="text-[11px] text-zinc-400">days</span>
    </label>
  );
}

/** "After: Cable Pulling, Panel Design" — a checklist of the other template tasks. */
export function TemplateDependsPicker({ item, items = [], onChange }) {
  const [open, setOpen] = useState(false);
  const others = items.filter((entry) => entry.id !== item.id);
  const chosen = Array.isArray(item.depends_on) ? item.depends_on.filter((id) => others.some((entry) => entry.id === id)) : [];
  const names = chosen.map((id) => others.find((entry) => entry.id === id)?.name).filter(Boolean);
  const toggle = (id, checked) => onChange(checked ? [...chosen, id] : chosen.filter((entry) => entry !== id));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-sm border border-zinc-700 bg-zinc-800/50 px-2.5 text-left text-[12.5px] text-zinc-100"
          aria-label={`Tasks that must finish before ${item.name || 'this task'}`}
          data-testid={`template-depends-${item.id}`}
        >
          <span className={cn('min-w-0 truncate', !names.length && 'text-zinc-400')}>{names.length ? `After ${names.join(', ')}` : 'No dependency'}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-400" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] p-2">
        <p className="px-1.5 pb-1.5 text-[11.5px] font-semibold text-ink-3">Starts after these finish</p>
        <ul className="max-h-[260px] space-y-0.5 overflow-y-auto">
          {others.map((entry) => (
            <li key={entry.id}>
              <label className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1.5 text-[13px] text-ink hover:bg-surface-hover">
                <Checkbox checked={chosen.includes(entry.id)} onCheckedChange={(checked) => toggle(entry.id, Boolean(checked))} />
                <span className="min-w-0 truncate">{entry.name || 'Task'}</span>
              </label>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
